import * as joint from "@clientio/rappid";
import { dia } from "@clientio/rappid";
import { ModelEventBus } from "../../../event-bus";
import { BaseNode, BaseView } from "./base-node";
import { Controller } from "./controller";
import { PreferenceStore } from "../../services/preference-store";

const Position = joint.ui.Halo.HandlePosition;

export class ElementController extends Controller {
  constructor(
    protected view: dia.CellView,
    protected context
  ) {
    super(view, context);
    this.view.model.on("change:size", this.onChangeSize.bind(this));
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  on(event: string, payload?: any): void {
    if (PreferenceStore.INSTANCE.isReadOnly) {
      return;
    }
    switch (event) {
      case "add":
        this.onAdd();
        break;
      case "remove":
        this.onRemove();
        break;
      case "change":
        this.onChange();
        break;
      case "cell:pointerdown":
        this.onCellPointerDown(payload?.evt);
        break;
      case "cell:pointerup":
        this.onCellPointerUp(payload?.evt);
        break;
      case "cell:pointerdblclick":
        this.onCellPointerDblClick(payload?.evt);
        break;
      case "element:pointerdown":
        this.onElementPointerDown(payload?.evt);
        break;
      case "element:pointermove":
        this.onElementPointerMove(payload?.evt);
        break;
      case "element:pointerup":
        this.onElementPointerUp(payload?.evt);
        break;
      case "element:drop":
        this.onElementDrop(payload?.evt);
        break;
      case "link:connect":
        this.onLinkConnect();
        break;
      case "link:disconnect":
        this.onLinkDisconnect();
        break;
      case "cell:validation":
        this.onCellValidation(payload?.errors, payload?.warnings);
    }
  }
  onChange() {}

  onCellPointerDown(evt?: joint.dia.Event) {
    //Do not show the halo on middle mouse panning
    if (evt.button === 1) return;
    this.createFreetransform();
    this.createHalo();
    const model = this.view.model;
  }

  onCellPointerUp(evt?: joint.dia.Event) {}

  onCellPointerDblClick(evt?: joint.dia.Event) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCellValidation(errors?: any, warnings?: any) {
    try {
      this.context.commandManager.stopListening();
      const cell = this.view.model as BaseNode;
      cell.errors = Array.from<string>(new Set(errors.map((e) => e.message)));
      cell.warnings = Array.from<string>(new Set(warnings.map((w) => w.message)));
      (this.view as BaseView).displayDiagnostics();
    } finally {
      this.context.commandManager.listen();
    }
  }

  createFreetransform() {
    const freeTransform = new joint.ui.FreeTransform({
      theme: "modern",
      cellView: this.view,
      resizeDirections: ["top-left", "top-right", "bottom-left", "bottom-right"],
      allowRotation: false,
      minWidth: (cell, FreeTransform, direction) => {
        const { width } = (this.view as BaseView).getMinSize(direction);
        return width;
      },
      minHeight: (cell, FreeTransform, direction) => {
        const { height } = (this.view as BaseView).getMinSize(direction);
        return height;
      },
      preserveAspectRatio: this.view.model.attributes["fixedRatio"],
    });
    freeTransform.render();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChangeSize(element: dia.Element, newSize: any, opt: any) {
    (this.view as BaseView).displayDiagnostics();
  }

  createHalo(opt?: joint.ui.Halo.Options): joint.ui.Halo {
    const halo = new joint.ui.Halo({
      cellView: this.view,
      handles: this.getHaloConfig(),
      useModelGeometry: true,
      boxContent: "",
      theme: "dark",
      rotateAngleGrid: 90,
      tinyThreshold: 0,
      smallThreshold: 1000,
      ...opt,
    });
    halo.on("action:remove:pointerdown", (evt) => {
      evt.preventDefault();
      this.view.model.remove({ mergeArrays: true });
    });
    /* @ts-expect-error TBD*/
    halo.makeLoopLink = function () {};

    halo.render();
    return halo;
  }

  getHaloConfig(): joint.ui.Halo.Handle[] {
    return [
      {
        name: "remove",
        position: Position.NW,
        events: { pointerdown: "" },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Remove the object",
            "data-tooltip-position": "right",
            "data-tooltip-padding": 25,
          },
        },
      },
      {
        name: "link-w",
        position: Position.W,
        events: {
          pointerdown: "startLinking",
          pointermove: "doLink",
          pointerup: "stopLinking",
        },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Create a transition",
            "data-tooltip-position": "left",
            "data-tooltip-padding": 25,
          },
        },
      },
      {
        name: "link-n",
        position: Position.N,
        events: {
          pointerdown: "startLinking",
          pointermove: "doLink",
          pointerup: "stopLinking",
        },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Create a transition",
            "data-tooltip-position": "top",
            "data-tooltip-padding": 25,
          },
        },
      },
      {
        name: "link-e",
        position: Position.E,
        events: {
          pointerdown: "startLinking",
          pointermove: "doLink",
          pointerup: "stopLinking",
        },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Create a transition",
            "data-tooltip-position": "right",
            "data-tooltip-padding": 25,
          },
        },
      },
      {
        name: "link-s",
        position: Position.S,
        events: {
          pointerdown: "startLinking",
          pointermove: "doLink",
          pointerup: "stopLinking",
        },
        attrs: {
          ".handle": {
            "data-tooltip-class-name": "small",
            "data-tooltip": "Create a transition",
            "data-tooltip-position": "bottom",
            "data-tooltip-padding": 25,
          },
        },
      },
    ];
  }

  onLinkConnect() {
    if (this.view.model instanceof BaseNode) {
      (this.view.model as BaseNode).updateTransitionPriorities();
    }
  }

  onLinkDisconnect() {
    if (this.view.model instanceof BaseNode) {
      (this.view.model as BaseNode).updateTransitionPriorities();
    }
  }
  onElementPointerMove(evt?: joint.dia.Event) {}
  onElementPointerDown(evt?: joint.dia.Event) {}
  onElementPointerUp(evt?: joint.dia.Event) {}
  onElementDrop(evt?: joint.dia.Event) {}
  onAdd() {}
  onRemove() {}
}

export function isInActiveSubdiagram(model): boolean {
  let current = model?.getParentCell?.();
  while (current) {
    if (current.attr(["subdiagram", "active"]) === true) {
      return true;
    }
    current = current.getParentCell?.();
  }
  return false;
}
